// @flow
import React from 'react';
import { toastError, toastSuccess } from 'bench-internal-theme';
import { showServerError } from 'bk-web-app';
import { useMutation, useQuery } from '@apollo/client';
// $FlowFixMe - TS file
import useV2Mutation from 'utils/useV2Mutation';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
// $FlowFixMe - TS file
import { SplitFeatureFlags } from '../../features';
import type { LedgerAccount } from '../types';
import {
  UPDATE_LEDGER_ACCOUNT,
  CREATE_LEDGER_ACCOUNT,
  GET_LEDGER_ACCOUNTS,
  GET_LEDGER_ACCOUNTS_BY_CLIENT,
  MAP_CONNECTION_TO_ACCOUNT,
  UNMAP_FDS_ACCOUNT_LEDGER,
  GET_FDS_CONNECTIONS,
} from '../queries';
// $FlowFixMe - TS file
import { apolloV2Client } from '../../../general-ledger/queries';
import { useClient } from '../../ClientContext';
import LedgerAccountSidebarForm from './LedgerAccountSidebarForm';
import {
  findFdsAccountByAccountId,
  findFdsAccountByLedgerId,
} from '../../../edit-ledger-form/util';

type LedgerAccountSidebarFormContainerProps = {
  closeSidebar: () => void,
  ledgerAccount: ?LedgerAccount,
};

const LedgerAccountSidebarFormContainer = ({
  closeSidebar,
  ledgerAccount,
}: LedgerAccountSidebarFormContainerProps) => {
  const client = useClient();
  const isEditing = ledgerAccount?.id;

  const [featureState] = useFeatureFlags();
  const {
    featureContext: { features },
  } = featureState;

  const isAssignCategoryImprovementEnabled =
    features[SplitFeatureFlags.ASSIGN_CATEGORY_IMPROVEMENT]?.value === FeatureValue.FEATURE_ON;

  // Use v2 query for refetch if feature flag is enabled, otherwise use v1
  const ledgerAccountsQuery = isAssignCategoryImprovementEnabled
    ? GET_LEDGER_ACCOUNTS_BY_CLIENT
    : GET_LEDGER_ACCOUNTS;

  const refetchQueries = [
    {
      query: ledgerAccountsQuery,
      variables: { clientId: client?.id },
      context: isAssignCategoryImprovementEnabled ? { useV2: true } : undefined,
    },
  ];
  const [mapConnectionToAccount, { loading: loadingMapConnectionToAccount }] = useMutation(
    MAP_CONNECTION_TO_ACCOUNT,
    {
      onCompleted: () => {
        toastSuccess(
          'Ledger Account Mapped',
          'Your ledger account has been mapped to the selected integrations.',
        );
        !isEditing && closeSidebar();
      },
      onError: error => {
        toastError(
          'An error occurred mapping this ledger account to the selected integrations.',
          showServerError(error),
        );
      },
    },
  );

  const [unmapFDSAccountLedger, { loading: loadingUnmapFDSAccountLedger }] = useMutation(
    UNMAP_FDS_ACCOUNT_LEDGER,
    {
      onCompleted: () => {
        toastSuccess(
          'Ledger Account Un-Mapped',
          'Your ledger account has been un-mapped to the changed integration connection.',
        );
        !isEditing && closeSidebar();
      },
      onError: error => {
        toastError('An error occurred updating this ledger account.', showServerError(error));
      },
      refetchQueries,
    },
  );

  const mapFdsConnection = (newFdsAccount, ledgerId) =>
    mapConnectionToAccount({
      variables: {
        clientId: client.id,
        connection: {
          source: newFdsAccount?.source,
          id: newFdsAccount?.id,
        },
        accountId: ledgerId,
      },
      refetchQueries,
    });

  const unmapFdsConnection = previousFdsAccountId =>
    unmapFDSAccountLedger({
      variables: {
        accountId: previousFdsAccountId,
        ledgerId: null,
        dryRun: false,
      },
      refetchQueries,
    });

  const handleFdsConnectionMapping = (newFdsAccount, previousFdsAccount, ledgerId) => {
    if (!newFdsAccount && previousFdsAccount) {
      return unmapFdsConnection(previousFdsAccount?.id);
    }

    if (newFdsAccount?.id !== previousFdsAccount?.ids) {
      return mapFdsConnection(newFdsAccount, ledgerId);
    }
  };

  const handleConnectionMapping = (payload, financialConnections, ledgerId) => {
    const newFdsAccount = findFdsAccountByAccountId(
      financialConnections,
      payload?.functionalInput?.fdsAccountId,
    );

    const previousFdsAccount = findFdsAccountByLedgerId(financialConnections, ledgerId);

    if (newFdsAccount || previousFdsAccount) {
      return handleFdsConnectionMapping(newFdsAccount, previousFdsAccount, ledgerId);
    }
  };

  const [createLedgerAccount, { loading: loadingCreateLedgerAccount }] = useV2Mutation(
    CREATE_LEDGER_ACCOUNT,
    {
      onCompleted: () => {
        toastSuccess('Ledger Account Created', 'Your ledger account has been created.');
      },
      onError: error => {
        toastError('An error occurred creating this ledger account.', showServerError(error));
      },
      refetchQueries,
      client: isAssignCategoryImprovementEnabled ? apolloV2Client : undefined,
    },
  );

  const [updateLedgerAccount, { loading: loadingUpdateLedgerAccount }] = useV2Mutation(
    UPDATE_LEDGER_ACCOUNT,
    {
      onCompleted: () => {
        toastSuccess('Ledger Account Updated', 'Your ledger account has been updated.');
      },
      onError: error => {
        toastError('An error occurred updating this ledger account.', showServerError(error));
      },
      refetchQueries,
      awaitRefetchQueries: true,
      client: isAssignCategoryImprovementEnabled ? apolloV2Client : undefined,
    },
  );

  const onSubmit = payload => {
    if (ledgerAccount && ledgerAccount.id) {
      return updateLedgerAccount({
        variables: {
          ledgerAccountInput: {
            ...payload,
            id: ledgerAccount.id,
          },
        },
      });
    }
    return createLedgerAccount({
      variables: {
        ledgerAccount: payload,
        clientId: client?.id,
      },
    });
  };

  const { data: dataGetFdsConnections } = useQuery(GET_FDS_CONNECTIONS, {
    variables: { clientId: client?.id },
  });

  const financialConnections = dataGetFdsConnections?.client?.financialConnections || [];

  return (
    <LedgerAccountSidebarForm
      closeSidebar={closeSidebar}
      isSubmitting={
        loadingCreateLedgerAccount ||
        loadingUpdateLedgerAccount ||
        loadingMapConnectionToAccount ||
        loadingUnmapFDSAccountLedger
      }
      ledgerAccount={ledgerAccount}
      onSubmit={payload =>
        onSubmit(payload).then(response =>
          handleConnectionMapping(
            payload,
            financialConnections,
            ledgerAccount?.id || response?.data?.createLedgerAccount?.id,
          ),
        )
      }
    />
  );
};

export default LedgerAccountSidebarFormContainer;
